import { useRef, useState, useCallback } from "react";
import { generarSesionCompleta } from "../sesion.logic";

const DEFAULT_PROGRESS = {
    mongo: { estado: "pendiente", progreso: 0, tiempo: 0 },
    word: { estado: "pendiente", progreso: 0, tiempo: 0 },
    pdf: { estado: "pendiente", progreso: 0, tiempo: 0 },
    html: { estado: "pendiente", progreso: 0, tiempo: 0 },
};

const useStep7Generation = () => {
    const [progress, setProgress] = useState(DEFAULT_PROGRESS);
    const progressRef = useRef(DEFAULT_PROGRESS);

    const [isGenerating, setIsGenerating] = useState(false);
    const [mongoId, setMongoId] = useState(null);
    const [error, setError] = useState(null);
    const [currentMongoStep, setCurrentMongoStep] = useState(null);

    // Usamos useCallback para que la referencia de la función sea estable
    const updateProgress = useCallback((step, estado, progreso, tiempo) => {
        setProgress((prev) => {
            const next = {
                ...prev,
                [step]: {
                    estado,
                    progreso: typeof progreso === "number" ? progreso : prev[step]?.progreso ?? 0,
                    tiempo: typeof tiempo === "number" ? tiempo : prev[step]?.tiempo ?? 0,
                },
            };
            progressRef.current = next;
            return next;
        });
    }, []);

    const markHtmlAsCompleted = (tiempo = 0) => {
        updateProgress("html", "completado", 100, tiempo);
    };

    const resetProgress = () => {
        setProgress(DEFAULT_PROGRESS);
        progressRef.current = DEFAULT_PROGRESS;
        setMongoId(null);
        setError(null);
        setCurrentMongoStep(null);
    };

    const restoreProgress = (storedProgress) => {
        if (!storedProgress) return;

        const restored = { ...DEFAULT_PROGRESS };

        Object.entries(storedProgress).forEach(([step, data]) => {
            restored[step] = {
                estado: data.estado,
                progreso: Number(data.progreso ?? 0),
                tiempo: Number(data.tiempo ?? 0),
            };
        });

        setProgress(restored);
        progressRef.current = restored;
    };

    const setExistingMongoId = (id) => {
        setMongoId(id || null);
    };

    const startGeneration = async ({ sesion }) => {
        if (isGenerating) return null;

        try {
            setIsGenerating(true);
            setError(null);

            // REANUDACIÓN: Solo reseteamos si es una sesión totalmente nueva.
            // Si ya hay un mongoId previo, evitamos el parpadeo visual.
            const existingId = mongoId || sesion.sesionGenerada_id || sesion.mongo_id;

            if (!existingId) {
                resetProgress();
            } else {
                setMongoId(existingId);
            }

            let createdId = null;

            await generarSesionCompleta({
                sesion,
                updateProgress,
                setCurrentMongoStep,
                onMongoCreated: (id) => {
                    createdId = id;
                    setMongoId(id);
                },
            });

            return createdId || existingId;
        } catch (e) {
            console.error("Error en generación Step7:", e);
            setError(e.message || "Ocurrió un error durante la generación.");
            throw e;
        } finally {
            setIsGenerating(false);

            // Verificación final de consistencia para el estado HTML
            setProgress(prev => {
                if (
                    prev.mongo.estado === "completado" &&
                    prev.word.estado === "completado" &&
                    prev.pdf.estado === "completado" &&
                    prev.html.estado !== "completado"
                ) {
                    const fixed = {
                        ...prev,
                        html: { estado: "completado", progreso: 100, tiempo: prev.html.tiempo }
                    };
                    progressRef.current = fixed;
                    return fixed;
                }
                return prev;
            });
        }
    };

    const getLatestProgress = () => progressRef.current;

    return {
        progress,
        isGenerating,
        mongoId,
        error,
        currentMongoStep,
        startGeneration,
        resetProgress,
        restoreProgress,
        setExistingMongoId,
        getLatestProgress,
        markHtmlAsCompleted
    };
};

export default useStep7Generation;